/* eslint-disable no-param-reassign */
import type { Dispatch, MouseEvent, MutableRefObject, RefObject, SetStateAction } from 'react';
import type React from 'react';
import { useCallback } from 'react';

import type { IState } from '../hooks/use-tabs-header';
import { MARGIN } from '../config';
import type { ITitle } from '../../../tabs/models';
import type { IBrowser } from '../../../../models';

interface IInputs {
    /** Браузер */
    browser: IBrowser;

    /** Координаты */
    coords: MutableRefObject<{ x: number; y: number }>;

    /** Ref на заголовок */
    headerContainer: RefObject<HTMLDivElement>;

    /** Ширина заголовка */
    headerContainerWidth: MutableRefObject<number>;

    /** Ref на список в заголовке */
    headerList: RefObject<HTMLDivElement>;

    /** Работает ли скрол */
    isScrollable: MutableRefObject<boolean>;

    /** Минимальный translateX */
    minxtranslateX: MutableRefObject<number>;

    /** Включена ли анимация */
    move: MutableRefObject<boolean>;

    /** Выбранный id */
    selectedID: string | number;

    /** Ref на Выбранный таб  */
    selectedTab: RefObject<HTMLDivElement>;

    /** Ф-ия установки стейта */
    setState: Dispatch<SetStateAction<IState>>;

    /** Старт */
    start: MutableRefObject<boolean>;

    /** Стейт */
    state: IState;

    /** Массив тайтлов */
    titles: ITitle[];
}

type TOnStart = Pick<MouseEvent, 'clientX' | 'clientY'>;

export interface ICustomWindow extends Window {
    /** Ф-ия DOMMatrix */
    DOMMatrix: new (init: string | number[]) => DOMMatrix;

    /** Ф-ия MSCSSMatrix */
    MSCSSMatrix: new (init: string | number[]) => DOMMatrix;

    /** Ф-ия WebKitCSSMatrix */
    WebKitCSSMatrix: new (init: string | number[]) => DOMMatrix;
}

declare let window: ICustomWindow;

/** Функция возвращает обработчики событий
 * Код ниже невозможно протестировать
 * потому что jest dom не реализовывает CSSMatrixConstructor
 * мокать слишком дорого и не имеет смысла
 */
/* istanbul ignore next */
export const useHandlers = ({
    browser,
    coords,
    headerContainer,
    headerContainerWidth,
    headerList,
    isScrollable,
    minxtranslateX,
    move,
    selectedID,
    selectedTab,
    setState,
    start,
    state,
    titles,
}: IInputs) => {
    const onStart = ({ clientX, clientY }: TOnStart) => {
        const headerListCurrent = headerList.current;

        if (!headerListCurrent) return;

        coords.current = {
            x: clientX,
            y: clientY,
        };

        start.current = true;

        const { transform } = window.getComputedStyle(headerListCurrent);

        const CSSMatrixConstructor = window.WebKitCSSMatrix;

        const { e: currentTranslateX } = new CSSMatrixConstructor(transform);

        setState(value => {
            const { translateX } = value;

            return {
                ...value,
                translateX: translateX === currentTranslateX ? translateX : currentTranslateX,
            };
        });
    };

    const onEnd = (clientX: number) => {
        if (!start.current) return;

        if (!move.current) {
            start.current = false;

            return;
        }

        move.current = false;
        start.current = false;

        setState(value => {
            let { translateX } = value;

            translateX += clientX - coords.current.x;

            coords.current = {
                ...coords.current,
                x: clientX,
            };

            if (translateX < minxtranslateX.current) {
                translateX = minxtranslateX.current;
            }

            if (translateX > 0) {
                translateX = 0;
            }

            return { ...value, translateX };
        });
    };

    const onTouchStart = (event: React.TouchEvent) => {
        if (!isScrollable.current) return;

        // eslint-disable-next-line prefer-destructuring
        const { clientX, clientY } = event.changedTouches[0];

        if (event.touches.length !== 1) return;

        onStart({ clientX, clientY });
    };

    const onTouchEnd = (event: React.TouchEvent) => {
        if (!isScrollable.current) return;

        // eslint-disable-next-line prefer-destructuring
        const { clientX } = event.changedTouches[0];

        onEnd(clientX);
    };

    const changePositionToSelectedID = useCallback(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        (selectedIDCurrent: number | string, prevSelectedID?: number | string) => {
            const tab = selectedTab.current;

            if (!tab) return;

            const id = titles.findIndex(title => selectedIDCurrent === title.id);
            let prevID = titles.findIndex(title => prevSelectedID === title.id);

            prevID = prevID === -1 ? 0 : prevID;

            const direction = prevID - id;

            const width = tab.offsetWidth;

            const parent = selectedTab.current?.parentElement;
            const parentX = parent?.getBoundingClientRect().left ?? 0;
            const tabX = selectedTab.current?.getBoundingClientRect().left ?? 0;
            const position = tabX - parentX;

            let translateX = 0;

            if (isScrollable.current) {
                if (direction < 0) {
                    translateX = MARGIN - position;

                    if (translateX < minxtranslateX.current) {
                        translateX = minxtranslateX.current;
                    }
                }

                if (direction > 0) {
                    translateX = headerContainerWidth.current - (position + width + MARGIN);

                    if (translateX > 0) {
                        translateX = 0;
                    }
                }
            }

            setState({
                selectedID,
                translateX,
                lineWidth: width,
                linePosition: position,
            });
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedTab.current, titles]
    );

    const onMove = (clientX: number) => {
        if (coords.current.x === clientX) return;

        move.current = true;

        setState(value => {
            let { translateX } = value;

            translateX += clientX - coords.current.x;

            coords.current = { ...coords.current, x: clientX };

            if (translateX < minxtranslateX.current - 100) {
                translateX = minxtranslateX.current - 100;
            }

            if (translateX > 100) {
                translateX = 100;
            }

            return { ...value, translateX };
        });
    };

    const onTouchMove = (event: TouchEvent) => {
        const [{ clientX, clientY }] = event.changedTouches;

        if (!start.current || event.touches.length !== 1) return;

        if (
            !move.current &&
            Math.abs(coords.current.x - clientX) < Math.abs(coords.current.y - clientY)
        ) {
            start.current = false;

            return;
        }

        event.preventDefault();

        onMove(clientX);
    };

    const addEventListener = () => {
        const headerContainerCurrent = headerContainer.current;

        if (!browser.mobile || !headerContainerCurrent) return;

        headerContainerCurrent.addEventListener('touchmove', onTouchMove);
    };

    const removeEventListener = () => {
        const headerContainerCurrent = headerContainer.current;

        if (!headerContainerCurrent) return;

        headerContainerCurrent.removeEventListener('touchmove', onTouchMove);
    };

    const init = () => {
        const headerContainerCurrent = headerContainer.current;
        const headerListCurrent = headerList.current;
        if (!headerContainerCurrent) return;

        if (!headerListCurrent) return;

        const headerListWidth = headerListCurrent.getBoundingClientRect().width;

        const headerContainerWidthCurrent = headerContainerCurrent.getBoundingClientRect().width;
        const { selectedID: selectedIDCurrent } = state;

        if (headerContainerWidth.current === headerContainerWidthCurrent) return;

        headerContainerWidth.current = headerContainerWidthCurrent;
        minxtranslateX.current = headerContainerWidthCurrent - headerListWidth;

        if (minxtranslateX.current >= 0) {
            isScrollable.current = false;
            removeEventListener();
        } else {
            isScrollable.current = true;
            addEventListener();
        }

        changePositionToSelectedID(selectedIDCurrent);
    };

    return {
        init,
        onTouchStart,
        onTouchEnd,
        changePositionToSelectedID,
    };
};
